/**
 * The handoff contract between the CRM (producer — the agent's "Accept" click) and the
 * `/credee-demo/agent-join` page (consumer).
 *
 * It lives here, next to the consumer, so the public demo never imports from a private dashboard
 * folder — and so the storage-key string exists exactly once. (The older observer handoff
 * duplicates its key in both files; that is the mistake this file avoids.)
 *
 * Nothing sensitive rides the URL: the CRM writes this payload to `localStorage` under a random
 * nonce and passes only the nonce as `?handoff=`.
 */

/** What the CRM stashes for the agent's new tab after Node issues a join ticket. */
export interface AgentJoinHandoff {
  /** The ROOM session id — what the room socket must subscribe with. */
  roomSessionId: string;
  /** The pre-approved guest identity, already `accepted` in `live_call_join_requests`. */
  participantId: string;
  /** Roster label, e.g. "Himanshu Saini (Agent)". */
  name: string;
  /** The room's product keyname (e.g. "practina"); rides `?token=` so the tab brands itself. */
  product?: string;
}

/** localStorage key prefix; the suffix is a random nonce. */
export const AGENT_JOIN_HANDOFF_PREFIX = 'credeeAgentHandoff';

/**
 * Query param + value marking a pre-approved seat (`?join=agent-join`): the call page skips the
 * mic/cam preferences screen and walks straight into the join gate.
 *
 * It is a SEPARATE param from `?token=`, which stays the product keyname. Earlier this value rode
 * `token` itself, so every agent/observer join reloaded under the default product's branding.
 */
export const AGENT_JOIN_PARAM = 'join';
export const AGENT_JOIN_TOKEN = 'agent-join';

/** sessionStorage key the guest-join component restores its identity from (per room). */
export function guestIdentityKey(roomSessionId: string): string {
  return `credeeRoomGuest:${roomSessionId}`;
}

/**
 * Read the one-shot handoff for `nonce`, mirroring the observer flow: the localStorage entry is
 * consumed on first read (so a leaked nonce is useless twice) but re-stashed in sessionStorage,
 * so refreshing this tab still boots.
 */
export function readAgentJoinHandoff(nonce: string | null): AgentJoinHandoff | null {
  if (!nonce) return null;
  const key = `${AGENT_JOIN_HANDOFF_PREFIX}:${nonce}`;
  try {
    const raw = localStorage.getItem(key) ?? sessionStorage.getItem(key);
    localStorage.removeItem(key);
    if (raw) sessionStorage.setItem(key, raw);
    return raw ? (JSON.parse(raw) as AgentJoinHandoff) : null;
  } catch {
    return null;
  }
}
